import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import apiService from '../../services/api';
import { AdminActivityLog } from '../../types';
import { formatDateTimeIst } from '../../utils/dateUtils';

const severityColor: Record<AdminActivityLog['severity'], 'success' | 'error' | 'warning' | 'info'> = {
  success: 'success',
  error: 'error',
  warning: 'warning',
  info: 'info',
};


const AdminActivityLogPanel: React.FC = () => {
  const [logs, setLogs] = useState<AdminActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState<AdminActivityLog['severity'] | ''>('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [messageFilter, setMessageFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
  });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getAdminActivityLogs({
        severity: severityFilter || undefined,
        source: sourceFilter || undefined,
        message: messageFilter || undefined,
        per_page: 10,
        page,
      });
      setLogs(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Failed to load admin activity logs:', error);
    } finally {
      setLoading(false);
    }
  }, [severityFilter, sourceFilter, messageFilter, page]);

  useEffect(() => {
    void fetchLogs();
  }, [fetchLogs]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleResetFilters = () => {
    setSeverityFilter('');
    setSourceFilter('');
    setMessageFilter('');
    setPage(1);
  };

  return (
    <Card sx={{ mt: 4 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Admin Activity Log
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Severity</InputLabel>
            <Select
              value={severityFilter}
              label="Severity"
              onChange={(event) => setSeverityFilter(event.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="info">Info</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Source"
            size="small"
            value={sourceFilter}
            onChange={(event) => setSourceFilter(event.target.value)}
          />
          <TextField
            label="Message contains"
            size="small"
            value={messageFilter}
            onChange={(event) => setMessageFilter(event.target.value)}
          />
          <Button variant="outlined" size="small" onClick={fetchLogs}>
            Apply
          </Button>
          <Button variant="text" size="small" onClick={handleResetFilters}>
            Reset
          </Button>
        </Box>
        {loading ? (
          <Typography variant="body2" color="textSecondary">
            Loading logs...
          </Typography>
        ) : logs.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No activity logged yet.
          </Typography>
        ) : (
          <>
            <List dense>
              {logs.map((entry) => (
                <ListItem key={entry.id} disableGutters>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1">{entry.message}</Typography>
                        <Chip
                          label={entry.severity}
                          size="small"
                          color={severityColor[entry.severity]}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="textSecondary">
                        {entry.source ? `${entry.source} · ` : ''}
                        {formatDateTimeIst(entry.created_at)}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={pagination.last_page || 1}
                page={page}
                onChange={handlePageChange}
                size="small"
              />
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminActivityLogPanel;
